import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ColumnProps } from 'antd/lib/table';
import { Button, Layout, Table, Tag, Space, Tooltip } from 'antd';
import { FormattedDate, FormattedNumber } from 'react-intl';
import { addLeadingChar } from '../../lib/addLeadingChar';
import { DateUpdateFlexRow } from '../../components/widgets/DateUpdateFlexRow';
import { FileProtectOutlined, FileDoneOutlined , ShoppingCartOutlined, ControlOutlined } from '@ant-design/icons';

import { PageTitle } from '../../components/layouts/page/PageTitle';
import { useOrders } from '../../hooks/data/useOrders';
import { OrderListItem } from '../../hooks/data/models';
import { api } from '../../api/api';
import { SWR_KEYS } from '../../hooks/data/swrKeys';


export const OrderListPage = () => {

  const navigate = useNavigate();
  const {orders, lastUpdated, refreshMainList} = useOrders();

  const exportToCsv = async () => {

    const expResults: Array<any> = await api.get(SWR_KEYS.ORDERS + '/export').then((response) => {return response.data;});

    // 1. Define the headers based on your interface
    const headers: Array<string> = [
      '_id', 
      'number', 
      'currency', 
      'amountTotal', 
      'orderType', 
      'paymentStatus', 
      'createdAt', 
      'hasInvoice',
      'customerFirstName',
      'customerLastName',
      'customerEmail',
      'customerLine1',
      'customerCity',
      'customerState',
      'customerCountry',
      'vendorName',
      'vendorEmail',
      'vendorLine1',
      'vendorCity',
      'vendorState',
      'vendorCountry'
    ];

    // 2. Build the CSV rows
    const csvRows = [];
    
    // Push the header row first
    csvRows.push(headers.join(','));

    // Map over orders and push each row
    for (const order of expResults) {
      const values = headers.map(header => {
        const value = order[header];
        
        // Handle undefined or null values
        if (value === undefined || value === null) {
          return '';
        }
        
        // Convert everything to string and handle commas/quotes to prevent breaking the CSV format
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          // Escape quotes by doubling them, and wrap the whole cell in quotes
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        
        return stringValue;
      });
      
      csvRows.push(values.join(','));
    }

    // 3. Combine rows with newline characters
    const csvString = csvRows.join('\n');

    // 4. Create a Blob and trigger the download
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    // Generate a filename with the current date
    const dateStr = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `orders_export_${dateStr}.csv`);
    
    // Append to body, click, and clean up
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Free up memory
  };


  const columns: ColumnProps<OrderListItem>[] = [
    {
      title: 'Order Number',
      dataIndex: 'number',
      key: 'number',
      render: (_, record) => (
         <Space>          
          {record.orderType == "adminOrder" && <Tooltip title="order created from the admin dashboard"><ControlOutlined /></Tooltip>}
          {record.orderType == "cartOrder" && <Tooltip title="order created from the online shop"><ShoppingCartOutlined /></Tooltip>}
          <span>{record.number? "#" + addLeadingChar(record.number.toString(), '0', 4) : "n/a"}</span>
         </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (_, record) => {

        const isPaid = record.paymentStatus === "fullyPaid";

        return(
          <Space size="small">
            {/* Render the Payment Tag (Green if paid, Gold otherwise) */}
            {isPaid ? (
              <Tag color="green"> paid </Tag>
            ) : (
              <Tag color="gold">{record.paymentStatus ?? "n/a"}</Tag>
            )}

            {/* Conditionally render the icons */}
            {record.hasCertificates && (
              <Tooltip title="Certificates loaded">
                <FileProtectOutlined />
              </Tooltip>
            )}
            {record.hasInvoice && (
              <Tooltip title="Invoice loaded">
                <FileDoneOutlined />
              </Tooltip>
            )}
          </Space>);
      }
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => (
        <FormattedDate value={text} day={'numeric'} month={'numeric'} year={'numeric'} />
      ),
    },
    {
      title: 'Price',
      key: 'price',
      render: (_, record) => (
        <FormattedNumber value={record.amountTotal} currency={record.currency} style={'currency'} />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (text: any, record) => (
        <span>
          <Link to={`/orders/detail/${record._id}`}>
            Detail
          </Link>
        </span>
      ),
    },
  ];

  return (
    <Layout.Content>
      <PageTitle
        title={'Orders'}
        actions={[
          <Space>
            <Button key='export-order-button' onClick={exportToCsv} title={'export'}> 
              export
            </Button>
            <Button key="new-order-button" onClick={() => navigate('/orders/create')} title={'new order'} type={'primary'}>
              new order
            </Button>
          </Space>,
        ]}
      />

      <DateUpdateFlexRow 
        lastUpdated={lastUpdated}
        refreshCallback={refreshMainList}
      />

      <Table loading={!orders} columns={columns} dataSource={orders} rowKey={(record) => record._id} />
    </Layout.Content>
  );
};
